import React, { useContext } from "react";
import { useQuery, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "@components/ui/use-toast";
import { Button } from "@components/ui/button";
import { useLocation } from "react-router-dom";
import { fetchPluginInfo, saveFullPluginInfo } from "../plugin/createPluginApi";
import PageLoader from "@components/loaders/PageLoader";
import { GeneralInfoForm } from "./GeneralInfoForm";
import { GeneralInfoView } from "./GeneralInfoView";
import { GeneralContext } from "@services/providers/GeneralProvider";
import { Cog, LibraryBig, MoveRight } from "lucide-react";
import { fetchSystemSettings } from "./createPluginApis";
import {
  MeshPluginActions,
  MeshPluginProvider,
  useMeshPluginContext,
} from "@services/providers/MeshPlugin";
import { useUnsavedChanges, UnsavedChangesDialog } from "./Unsavedchangesguard";

type GeneralInfo = {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: { minimum: string };
  type: string;
  author: string;
  productType: string;
  ls_product_type: string[];
  pluginDependency: { name: string; id: string }[];
  pluginLogo: string;
  id?: string | null;
  createdDate?: string | null;
  updatedDate?: string | null;
  interpreter?: {
    type: string;
    version: string;
  };
  lsUser?: string;
};

const CreatePlugin: React.FC = () => {
  const [pluginId, setPluginId] = React.useState<string | null>(null);
  const [isEdit, setIsEdit] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [showValidation, setShowValidation] = React.useState(false);
  const [originalPluginInfo, setOriginalPluginInfo] =
    React.useState<GeneralInfo | null>(null);
  const [pluginGeneralInfo, setPluginGeneralInfo] = React.useState<GeneralInfo>(
    {
      pluginName: "",
      version: "",
      tags: [],
      description1: "",
      description2: "",
      ls_version: { minimum: "" },
      type: "",
      author: "",
      productType: "",
      ls_product_type: [],
      pluginDependency: [],
      pluginLogo: "",
      id: "",
      createdDate: "",
      updatedDate: "",
    },
  );

  const { productType } = useContext(GeneralContext);
  const { hasUIPage, dispatch } = useMeshPluginContext();
  const isDirty =
    originalPluginInfo !== null &&
    JSON.stringify(pluginGeneralInfo) !== JSON.stringify(originalPluginInfo);

  const pendingNavigationRef = React.useRef(false);

  React.useEffect(() => {
    if (productType !== "cmx") {
      window.location.href = "/";
    }
  }, [productType]);

  React.useEffect(() => {
    if (!pluginId) {
      const storedPluginId =
        sessionStorage.getItem("meshPluginId") ||
        sessionStorage.getItem("editPluginId") ||
        null;
      setPluginId(storedPluginId);
    }
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isEditIntent =
      location.state?.isEdit === true || params.get("edit") === "1";

    const meshPluginId = sessionStorage.getItem("meshPluginId");
    const editPluginId = sessionStorage.getItem("editPluginId");
    const resolvedPluginId =
      meshPluginId || editPluginId || location.state?.pluginId || pluginId;

    if (isEditIntent && !resolvedPluginId) {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
    }
  }, [location.search, location.state, pluginId, navigate]);

  React.useEffect(() => {
    const handlePopState = () => {
      sessionStorage.removeItem("activeMeshFlow");
      navigate("/ai-engine", { replace: true });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  React.useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("activeMeshFlow");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleGeneralInfoChange = (fields: Partial<GeneralInfo>) => {
    setPluginGeneralInfo((prev) => ({ ...prev, ...fields }));
  };

  React.useEffect(() => {
    if (location.state?.pluginId) {
      setPluginId(location.state.pluginId);
      setIsEdit(location.state.isEdit ?? true);
      setIsEditing(false);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const edit = params.get("edit");
    const editPluginId = sessionStorage.getItem("editPluginId");

    if (edit === "1" && editPluginId) {
      setPluginId(editPluginId);
      setIsEdit(true);
      setIsEditing(false);
      return;
    } else if (pluginId !== null) {
      alert(
        "No plugin ID found for editing. Redirecting to create new plugin.",
      );
      return;
    }

    setPluginId(null);
    setIsEdit(false);
    setIsEditing(true);
  }, [location.state]);

  const { data: pluginInfoData, isLoading: isFetchingPluginInfo } = useQuery({
    queryKey: ["pluginInfo", pluginId],
    queryFn: () => fetchPluginInfo(pluginId!),
    enabled: !!pluginId && isEdit,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!pluginInfoData) return;

    const data = pluginInfoData;

    dispatch({
      type: MeshPluginActions.SET_HAS_UI_PAGE,
      payload: !!data?.is_ui_page,
    });

    dispatch({
      type: MeshPluginActions.SET_HAS_LOGIC_PAGE,
      payload: !!data?.is_logic_page,
    });

    dispatch({
      type: MeshPluginActions.SET_HAS_TEMP_CONFIG_PAGE,
      payload: !!data?.temp_config_page,
    });
  }, [pluginInfoData]);

  // Fetch system version from Redis via basic settings endpoint
  const { data: systemSettingsData } = useQuery({
    queryKey: ["systemSettings"],
    queryFn: fetchSystemSettings,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!pluginInfoData) return;
    const data = pluginInfoData.data || pluginInfoData;
    if (!data) {
      toast({
        variant: "destructive",
        title: "Error Fetching Plugin Info",
        description: "No data found for the given plugin ID.",
        duration: 3000,
      });
      return;
    }
    const loadedData = {
      pluginName: data.pluginName || "",
      version: data.version || "",
      tags: data.tags || [],
      description1: data.description1 || "",
      description2: data.description2 || "",
      ls_version: data.ls_version || { minimum: "4.7.7" },
      author: data.author || "",
      productType: data.productType || "",
      ls_product_type: data.ls_product_type || [],
      pluginDependency: data.pluginDependency || [],
      pluginLogo: data.pluginLogo || "",
      type: data.type || "",
      id: data.id || "",
      createdDate: data.createdDate || "",
      updatedDate: data.updatedDate || "",
    };
    setPluginGeneralInfo(loadedData);
    setOriginalPluginInfo(JSON.parse(JSON.stringify(loadedData)));
  }, [pluginInfoData]);

  const validateGeneralInfo = (info: GeneralInfo) => {
    const hasHtml = (val: string) => /<[^>]*>/g.test(val);
    const hasSlash = (val: string) => val.includes("/") || val.includes("\\");

    if (hasHtml(info.pluginName))
      return "Plugin name cannot contain HTML tags.";
    if (hasSlash(info.pluginName)) return "Plugin name cannot contain slashes.";
    if (hasHtml(info.author)) return "Author cannot contain HTML tags.";
    if (hasSlash(info.author)) return "Author cannot contain slashes.";
    if (hasHtml(info.description1))
      return "Plugin Description cannot contain HTML tags.";
    if (hasHtml(info.description2))
      return "Plugin Development Description cannot contain HTML tags.";
    if (info.tags.some(hasHtml)) return "Tags cannot contain HTML tags.";
    if (info.tags.some(hasSlash)) return "Tags cannot contain slashes.";

    if (!info.pluginName?.trim()) return "Plugin Name is required.";
    if (!info.version?.trim()) return "Plugin Version is required.";
    if (!info.tags || !info.tags.length) return "At least one tag is required.";
    if (!info.description1?.trim() || info.description1.length < 15)
      return "Plugin Description is required (min 15 chars).";
    if (!info.description2?.trim() || info.description2.length < 15)
      return "Plugin Development Description is required (min 15 chars).";
    if (!info.ls_version?.minimum) return "LS Version is required.";
    if (!info.type?.trim()) return "Type is required.";
    if (!info.author?.trim()) return "Author is required.";
    if (!info.productType?.trim()) return "Product Type is required.";
    if (!info.ls_product_type || !info.ls_product_type.length)
      return "At least one LS Product Type is required.";
    return null;
  };

  const sanitizeText = (value: string): string => {
    return value
      .replace(/<[^>]*>/g, "")
      .split("/")
      .join("")
      .split("\\")
      .join("")
      .trim();
  };

  // ← mutation declared before useUnsavedChanges so isSaving is available
  const { mutate: savePluginInfo, isLoading: isSaving } = useMutation({
    mutationFn: saveFullPluginInfo,
    onSuccess: (_data, variables) => {
      const id = variables.id;
      if (id) sessionStorage.setItem("meshPluginId", id);

      setShowValidation(false);
      setOriginalPluginInfo(JSON.parse(JSON.stringify(pluginGeneralInfo)));

      toast({
        variant: "success",
        title: `Plugin Info ${isEdit ? "Updated !" : "Saved !"}`,
        description: `General plugin information ${isEdit ? "updated" : "saved"} successfully.`,
        duration: 3000,
      });

      if (pendingNavigationRef.current) {
        pendingNavigationRef.current = false;
        confirmNavigation();
      } else {
        navigate(`/create-node-plugin/ui?edit=1`, {
          replace: true,
          state: { isEdit },
        });
      }
    },
    onError: (err: any) => {
      pendingNavigationRef.current = false;
      let errorMessage = "Could not connect.";
      if (err?.response?.data) {
        try {
          const data = err.response.data;
          if (typeof data.message === "string" && data.message.includes("{")) {
            const inner = JSON.parse(data.message.split(" - ")[1] || "{}");
            errorMessage =
              inner.error_description || inner.error || data.message;
          } else {
            errorMessage =
              data.error_description || data.message || errorMessage;
          }
        } catch {
          console.warn("Failed to parse error message.");
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      toast({
        variant: "destructive",
        title: "Error Saving Plugin Info",
        description: errorMessage,
        duration: 3000,
      });
    },
  });

  // ← now declared after mutation so isSaving is defined
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    isDirty && !isSaving,
  );

  const handleSaveAndContinue = () => {
    const error = validateGeneralInfo(pluginGeneralInfo);
    if (error) {
      setShowValidation(true);
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: error,
        duration: 4000,
      });
      return;
    }

    const sanitized = {
      ...pluginGeneralInfo,
      pluginName: sanitizeText(pluginGeneralInfo.pluginName),
      version: sanitizeText(pluginGeneralInfo.version),
      author: sanitizeText(pluginGeneralInfo.author),
      description1: sanitizeText(pluginGeneralInfo.description1),
      description2: sanitizeText(pluginGeneralInfo.description2),
      tags: pluginGeneralInfo.tags.map(sanitizeText),
    };

    setPluginGeneralInfo(sanitized);

    if (isEdit && originalPluginInfo) {
      const hasChanges =
        JSON.stringify(sanitized) !== JSON.stringify(originalPluginInfo);
      if (!hasChanges) {
        navigate(`/create-node-plugin/ui?edit=1`, {
          replace: true,
          state: { isEdit: hasUIPage ?? false },
        });
        return;
      }
    }

    const now = Date.now().toString();
    const updatedFields: Partial<GeneralInfo> = {};
    const alreadySaved = !!sanitized.id;

    if (!sanitized.id) {
      updatedFields.id = `${sanitized.pluginName.toLowerCase().replace(/\s+/g, "")}_generic_${now}`;
    }

    if (!sanitized.createdDate) {
      updatedFields.createdDate = now;
    }

    updatedFields.updatedDate = now;

    const systemVersion = systemSettingsData?.data?.version;
    let meshVersion: string | null = "2.0";

    if (systemVersion) {
      const compareVersions = (version: string, target: string): number => {
        const v1 = version.split(".").map(Number);
        const v2 = target.split(".").map(Number);
        for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
          const num1 = v1[i] || 0;
          const num2 = v2[i] || 0;
          if (num1 > num2) return 1;
          if (num1 < num2) return -1;
        }
        return 0;
      };
      meshVersion = compareVersions(systemVersion, "4.7.8") >= 0 ? "2.0" : null;
    }

    const finalData: any = {
      ...sanitized,
      ...updatedFields,
      is_edit: alreadySaved,
      interpreter: { type: "python", version: "3" },
      lsUser: "admin",
      pluginDependency: sanitized.pluginDependency.flat(),
      ls_product_type: sanitized.ls_product_type.flat(),
      pluginLogo: sanitized.pluginLogo ?? "",
      is_node: true,
      mesh_version: meshVersion,
    };

    if ((updatedFields.id || sanitized.id || "") !== pluginId) {
      setPluginId(updatedFields.id || sanitized.id || "");
    }

    savePluginInfo(finalData);
  };

  return (
    <div className="bg-gradient-to-br from-background via-muted/30 to-background flex items-center justify-center h-[calc(100vh-4rem)]">
      <div className="mx-auto max-w-6xl space-y-4 flex flex-col justify-center">
        <PageLoader show={isSaving} />

        <div className="space-y-2 w-full flex items-center gap-2 p-2 text-muted-foreground justify-center ">
          <LibraryBig size={22} />
          <p className="text-2xl pb-0 !mt-0">General Information.</p>
        </div>

        <div className="rounded-2xl border border-secondary bg-background shadow-md backdrop-blur-sm p-3 transition-all duration-300 !mt-2 w-[900px]">
          {!isEditing ? (
            <GeneralInfoView
              pluginName={pluginGeneralInfo.pluginName}
              version={pluginGeneralInfo.version}
              tags={pluginGeneralInfo.tags}
              description1={pluginGeneralInfo.description1}
              description2={pluginGeneralInfo.description2}
              ls_version={pluginGeneralInfo.ls_version}
              type={pluginGeneralInfo.type}
              author={pluginGeneralInfo.author}
              productType={pluginGeneralInfo.productType}
              ls_product_type={pluginGeneralInfo.ls_product_type}
              pluginDependency={pluginGeneralInfo.pluginDependency}
              pluginLogo={pluginGeneralInfo.pluginLogo}
              onEdit={() => {
                setIsEditing(true);
                setShowValidation(false);
              }}
              isNewPlugin={!isEdit}
            />
          ) : (
            <GeneralInfoForm
              {...pluginGeneralInfo}
              onChange={handleGeneralInfoChange}
              showValidation={showValidation}
            />
          )}

          <UnsavedChangesDialog
            open={showDialog}
            isSaving={isSaving}
            onCancel={cancelNavigation}
            onDiscard={() => {
              if (originalPluginInfo)
                setPluginGeneralInfo({ ...originalPluginInfo });
              confirmNavigation();
            }}
            onSave={() => {
              pendingNavigationRef.current = true;
              handleSaveAndContinue();
            }}
          />
        </div>

        <div className="sticky bottom-6 flex justify-end gap-3">
          <div className="flex justify-between items-center w-full">
            <Button
              className="px-6 flex gap-2 bg-background/80 hover:bg-background text-foreground border border-muted-foreground hover:text-primary hover:border-primary"
              onClick={() => {
                sessionStorage.removeItem("activeMeshFlow");
                navigate("/ai-engine", {
                  state: { pluginId, isEdit },
                  replace: true,
                });
              }}
              disabled={isSaving}
            >
              <Cog size={20} />
              Return to AI Engine
            </Button>

            <div className="flex items-center gap-2">
              {isEditing && isEdit && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setShowValidation(false);
                    if (originalPluginInfo) {
                      setPluginGeneralInfo(
                        JSON.parse(JSON.stringify(originalPluginInfo)),
                      );
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                className="px-6 flex gap-3"
                onClick={handleSaveAndContinue}
                disabled={isSaving || isFetchingPluginInfo}
              >
                {isEdit
                  ? originalPluginInfo &&
                    JSON.stringify(pluginGeneralInfo) ===
                      JSON.stringify(originalPluginInfo)
                    ? "Continue to UI Page"
                    : "Update Info & Continue"
                  : "Save Info & Start"}{" "}
                <MoveRight size={20} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateMeshPlugin: React.FC = () => {
  return (
    <MeshPluginProvider>
      <CreatePlugin />
    </MeshPluginProvider>
  );
};

export default CreateMeshPlugin;
